import { useState, useEffect } from 'react';
import { Plus, AlertCircle, CheckCircle, Loader, Info, Save } from 'lucide-react';
import { API_BASE, fetchApi, getAuthHeaders } from '../../services/apiClient';

/** One signpost row: where an entity type's names resolve to codes.
 *  The backend reads this table fresh on every seeding run — saves here
 *  apply to the very next ticket, no restart. */
interface LookupSourceRow {
  id: number;
  entity_type_name: string;
  carrier_code: string | null;
  lookup_table_fqn: string;
  lookup_code_column: string;
  lookup_name_columns: string;
  is_active: boolean;
  created_by: string;
}

interface DraftRow {
  entity_type_name: string;
  carrier_code: string;
  lookup_table_fqn: string;
  lookup_code_column: string;
  lookup_name_columns: string;
}

const EMPTY_DRAFT: DraftRow = {
  entity_type_name: '',
  carrier_code: '',
  lookup_table_fqn: '',
  lookup_code_column: '',
  lookup_name_columns: '',
};

export default function MasterLookupsEditor() {
  const [rows, setRows] = useState<LookupSourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [draft, setDraft] = useState<DraftRow>(EMPTY_DRAFT);
  const [savingId, setSavingId] = useState<number | 'new' | null>(null);
  const [edits, setEdits] = useState<Record<number, LookupSourceRow>>({});

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const response = await fetchApi(`${API_BASE}/admin/entity-lookup-sources`, { headers });
      if (!response.ok) throw new Error(`Failed to load lookup sources: ${response.statusText}`);
      setRows(await response.json());
      setEdits({});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const flashSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleCreate = async () => {
    setSavingId('new');
    setError('');
    try {
      const headers = await getAuthHeaders();
      const response = await fetchApi(`${API_BASE}/admin/entity-lookup-sources`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_type_name: draft.entity_type_name,
          carrier_code: draft.carrier_code || null,
          lookup_table_fqn: draft.lookup_table_fqn,
          lookup_code_column: draft.lookup_code_column,
          lookup_name_columns: draft.lookup_name_columns,
        }),
      });
      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || response.statusText);
      }
      setDraft(EMPTY_DRAFT);
      flashSuccess('Lookup added — applies from the next ticket run.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveRow = async (row: LookupSourceRow) => {
    setSavingId(row.id);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const response = await fetchApi(`${API_BASE}/admin/entity-lookup-sources/${row.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lookup_table_fqn: row.lookup_table_fqn,
          lookup_code_column: row.lookup_code_column,
          lookup_name_columns: row.lookup_name_columns,
          is_active: row.is_active,
        }),
      });
      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || response.statusText);
      }
      flashSuccess('Saved — applies from the next ticket run.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingId(null);
    }
  };

  const edited = (row: LookupSourceRow): LookupSourceRow => edits[row.id] ?? row;
  const setRowEdit = (row: LookupSourceRow, patch: Partial<LookupSourceRow>) =>
    setEdits((prev) => ({ ...prev, [row.id]: { ...edited(row), ...patch } }));

  const draftComplete =
    draft.entity_type_name.trim() &&
    draft.lookup_table_fqn.trim() &&
    draft.lookup_code_column.trim() &&
    draft.lookup_name_columns.trim();

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 p-6">
        <Loader size={16} className="animate-spin" /> Loading master lookups...
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Master Lookups</h2>
        <p className="text-sm text-gray-500">
          Where each entity type's names resolve to codes. Lookups always query the LIVE LRP
          master — nothing is copied locally, so results can never go stale.
        </p>
      </div>

      <div className="flex items-start gap-2 p-3 mb-4 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
        <Info size={16} className="mt-0.5 shrink-0" />
        <span>
          Changes apply on the <b>next ticket run automatically</b> — no restart. Carrier is
          optional: leave blank for a default that serves every client; set a JIRA project prefix
          (e.g. <code>FMSC</code>) to override for that client only.
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <AlertCircle size={16} /> {error}
        </div>
      )}
      {successMessage && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 rounded text-sm text-green-700">
          <CheckCircle size={16} /> {successMessage}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Entity type</th>
              <th className="px-3 py-2">Carrier</th>
              <th className="px-3 py-2">LRP master table</th>
              <th className="px-3 py-2">Code column</th>
              <th className="px-3 py-2">Name columns</th>
              <th className="px-3 py-2">Active</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => {
              const r = edited(row);
              const dirty = edits[row.id] !== undefined;
              return (
                <tr key={row.id} className={r.is_active ? '' : 'opacity-50'}>
                  <td className="px-3 py-2 font-medium text-gray-800">{r.entity_type_name}</td>
                  <td className="px-3 py-2 text-gray-500">{r.carrier_code ?? 'all'}</td>
                  <td className="px-3 py-2">
                    <input
                      className="w-full border border-gray-200 rounded px-2 py-1 font-mono text-xs"
                      value={r.lookup_table_fqn}
                      onChange={(e) => setRowEdit(row, { lookup_table_fqn: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className="w-32 border border-gray-200 rounded px-2 py-1 font-mono text-xs"
                      value={r.lookup_code_column}
                      onChange={(e) => setRowEdit(row, { lookup_code_column: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className="w-full border border-gray-200 rounded px-2 py-1 font-mono text-xs"
                      value={r.lookup_name_columns}
                      onChange={(e) => setRowEdit(row, { lookup_name_columns: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={r.is_active}
                      onChange={(e) => setRowEdit(row, { is_active: e.target.checked })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => handleSaveRow(r)}
                      disabled={!dirty || savingId === row.id}
                      className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-blue-600 text-white disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      {savingId === row.id ? (
                        <Loader size={12} className="animate-spin" />
                      ) : (
                        <Save size={12} />
                      )}
                      Save
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-400">
                  No lookup sources yet — run migration 023 or add one below.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
        <div className="text-sm font-medium text-gray-800 mb-2">Add lookup</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <input
            placeholder="Entity type (e.g. Vendor)"
            className="border border-gray-200 rounded px-2 py-1.5 text-sm"
            value={draft.entity_type_name}
            onChange={(e) => setDraft({ ...draft, entity_type_name: e.target.value })}
          />
          <input
            placeholder="Carrier (blank = all)"
            className="border border-gray-200 rounded px-2 py-1.5 text-sm"
            value={draft.carrier_code}
            onChange={(e) => setDraft({ ...draft, carrier_code: e.target.value })}
          />
          <input
            placeholder="Masters.dbo.vendormaster"
            className="border border-gray-200 rounded px-2 py-1.5 text-sm font-mono"
            value={draft.lookup_table_fqn}
            onChange={(e) => setDraft({ ...draft, lookup_table_fqn: e.target.value })}
          />
          <input
            placeholder="Code column"
            className="border border-gray-200 rounded px-2 py-1.5 text-sm font-mono"
            value={draft.lookup_code_column}
            onChange={(e) => setDraft({ ...draft, lookup_code_column: e.target.value })}
          />
          <input
            placeholder="Name columns (comma-separated)"
            className="border border-gray-200 rounded px-2 py-1.5 text-sm font-mono"
            value={draft.lookup_name_columns}
            onChange={(e) => setDraft({ ...draft, lookup_name_columns: e.target.value })}
          />
        </div>
        <button
          onClick={handleCreate}
          disabled={!draftComplete || savingId === 'new'}
          className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:bg-gray-200 disabled:text-gray-400"
        >
          {savingId === 'new' ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
          Add lookup
        </button>
      </div>
    </div>
  );
}
